import * as React from 'react';

import type { Article } from 'schema-dts';

import config from 'js/app/config';

import GoogleMarkup from 'bundles/seo/components/GoogleMarkup';
import { getTimeRequiredString } from 'bundles/seo/utils/schemaMarkupUtils';
import type { AuthorProfile } from 'bundles/shared-articles/components/ArticleAuthorProfile';
import {
  createAuthorsSchema,
  createBreadcrumbsListSchema,
  createGraph,
} from 'bundles/shared-articles/components/seo/utils/schemaMarkupUtils';
import type { Breadcrumb } from 'bundles/shared-articles/components/seo/utils/schemaMarkupUtils';

type InputProps = {
  title: string;
  createdAt?: string;
  updatedAt?: string;
  imageUrls?: Array<string | null | undefined> | [];
  estimatedReadTime?: number;
  breadcrumbs: Breadcrumb[];
  id: string;
  authors?: AuthorProfile[];
};

type Props = InputProps;

/**
 * Generates Google Schema Markup for an article.
 * @param {Object} props - The properties for the article.
 * @param {string} props.title - The title of the article.
 * @param {string} props.createdAt - The creation date of the article in ISO format.
 * @param {string} props.updatedAt - The last updated date of the article in ISO format.
 * @param {string[]} [props.imageUrls] - Array of image URLs related to the article.
 * @param {number} [props.estimatedReadTime] - Estimated read time in minutes.
 * @param {Object[]} props.breadcrumbs - Array of breadcrumb objects for navigation.
 * @param {string} props.id - The unique identifier for the article.
 * @param {Object[]} props.authors - Array of author objects.
 * @returns {React.Element} The Google Markup component.
 */
export const ArticleGoogleSchemaMarkup = ({
  title,
  createdAt,
  updatedAt,
  imageUrls,
  estimatedReadTime,
  breadcrumbs,
  id,
  authors,
}: Props) => {
  const authorsSchema = createAuthorsSchema(authors || []);

  const articleType: Article = {
    '@id': id,
    '@type': 'Article',
    headline: title,
    image: imageUrls?.length && imageUrls[0] ? imageUrls[0] : undefined,
    datePublished: createdAt,
    dateModified: updatedAt,
    timeRequired: estimatedReadTime ? getTimeRequiredString({ milliseconds: estimatedReadTime * 60000 }) : undefined,
    author: authorsSchema,
    publisher: {
      '@type': 'Organization',
      name: 'Coursera',
      url: `${config.url.base}`,
      logo: `${config.url.imageProxyRoot}https://coursera.s3.amazonaws.com/media/coursera-logo-square.png`,
    },
  };

  const breadcrumbType = createBreadcrumbsListSchema(breadcrumbs);

  return <GoogleMarkup graph={createGraph([articleType, breadcrumbType])} />;
};

export default ArticleGoogleSchemaMarkup;
